import _ from "lodash";
import { generateRandomId } from "Utils";
import { EVALUATION_MODE_CONFIG, HOTSPOT_CONFIG } from "../Konva/KonvaConfig";
import { KONVA_ATTR, KONVA_ITEM_TYPE } from "../Konva/KonvaConstants";
import {
  isTextNode,
  isImageNode,
  isPolygonNode,
  isEllipseNode,
  isRectangleNode,
  drawEllipseEventListener,
  drawPolygonEventListener,
  drawRectangleEventListener,
} from "../Konva/KonvaElements";
import { listenBackEvents, getCanvasRelativeSize } from "../KonvaCommonUtils";
import {
  updateTransformerHandler,
  maintainTransformationScale,
} from "../Konva/KonvaUtils";

const SWIPE_THRESHOLD = 10;
const STUDENT_MARKED_BOOKMARK = "-student-hotspot-bookmark-id";
const TEACHER_MARKED_BOOKMARK = "-teacher-marked-hotspot-tick";

const cachedTickImage = {},
  cachedBookmarkIcons = {};
let allowStudentClick = false;

const EVALUATION_STATUS = {
  CORRECT: "CORRECT",
  WRONG: "WRONG",
};

/**
 *
 * @param { stage, layer, transformer, ...callbacks }
 * @description konva callback to find hotspot action's
 * @description event listener for TEACHER CREATE mode
 */
const addTeacherCreateModeEventListener = props => {
  const { konva, stage, layer, transformer, drawMode } = props;

  const isDrawing = stage.getAttr(KONVA_ATTR.IS_DRAWING_POLYGON);

  if (isDrawing) {
    // * for drawing mode, listening events specifically shape wise
    const type = _.get(drawMode, "type", KONVA_ITEM_TYPE.RECTANGLE);
    const DRAW_EVENT_LISTENERS = {
      [KONVA_ITEM_TYPE.RECTANGLE]: drawRectangleEventListener,
      [KONVA_ITEM_TYPE.ELLIPSE]: drawEllipseEventListener,
      [KONVA_ITEM_TYPE.POLYGON]: drawPolygonEventListener,
    };
    const drawingEventListeners = _.get(DRAW_EVENT_LISTENERS, type);
    if (_.isFunction(drawingEventListeners)) drawingEventListeners(props);
    return;
  }

  // * Change cursor on mouse hover
  const onMouseOver = e => {
    const node = e.target;
    if (node instanceof konva.Shape) {
      stage.container().style.cursor = "pointer";
    } else {
      stage.container().style.cursor = "default";
    }
  };

  // * add transfomer on clicking shape
  const onClick = e => {
    const node = e.target;
    updateTransformerHandler(node, props);
  };

  // * check if marked tick exist hide it
  const onDragStart = e => {
    const node = e.target;
    const tickNode = stage.findOne(`#${node.id()}${TEACHER_MARKED_BOOKMARK}`);
    if (tickNode) {
      tickNode.visible(false);
      tickNode.destroy();
    }
  };

  // * fire ondrop event when item is dropped
  const onDragEnd = e => {
    const node = e.target;
    const payload = { id: node.id(), x: node.x(), y: node.y() };
    node.moveTo(layer);

    if (node instanceof konva.Shape) props.onDragEnd(payload);
  };

  // * maintain shape scale on transform
  const onTransform = e => {
    const node = e.target;
    if (!node) return;

    maintainTransformationScale(node, props);
  };

  // * give a callback on transform end with updated shape props
  const onTransformEnd = e => {
    const node = e.target;

    const isText = isTextNode(node, konva);
    const isImage = isImageNode(node, konva);
    const isEllipse = isEllipseNode(node, konva);
    const isPolygon = isPolygonNode(node, konva);
    const isRectangle = isRectangleNode(node, konva);

    const payload = {
      id: node.id(),
      x: node.x(),
      y: node.y(),
    };

    if (isRectangle || isImage) {
      _.set(payload, "width", node.width());
      _.set(payload, "height", node.height());
    } else if (isEllipse) {
      _.set(payload, "radiusX", node.radiusX());
      _.set(payload, "radiusY", node.radiusY());
    } else if (isText) {
      _.set(payload, "fontSize", node.fontSize());
    } else if (isPolygon) {
      _.set(payload, "points", node.points());
    }

    props.onTransform(payload);
  };

  listenBackEvents(stage, transformer, {
    onClick,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onTransform,
    onTransformEnd,
  });

  return {};
};

/**
 *
 * @param { stage, layer, transformer, ...callbacks }
 * @description konva callback to find hotspot action's
 * @description event listener for TEACHER PREVIEW and STUDENT EDIT mode
 */
const addEditAndPreviewModeEventListener = props => {
  const { stage, transformer, options } = props;
  if (!stage) return;
  stage.container().style.cursor = "pointer";
  let activeHotspot = null;
  let submissionData = _.get(options, "studentAnswers", []);

  let touchMoved = false;
  let touchStartPos = { x: 0, y: 0 };

  setTimeout(() => {
    // * allowing stage to settle down, before accepting any action on redraw
    allowStudentClick = true;
  }, 100);

  /**
   *
   * On clicking hotspot, add as a student response
   */
  const onClick = e => {
    if (touchMoved) {
      // * prevent click callback on swipe action
      e.cancelBubble = true;
      return;
    }

    if (!allowStudentClick) return;
    const node = e.target;

    const allowToShowHotspot = _.get(options, "allowToShowHotspot", false);
    const allowToSelectMultipleHotspot = _.get(
      options,
      "allowToSelectMultipleHotspot"
    );

    const id = node.id();
    const isRemoveAction = _.includes(id, STUDENT_MARKED_BOOKMARK);
    const isDropzone = _.first(_.split(node.name(), "#")) === "DROPZONE";

    if (allowToShowHotspot && !isDropzone) {
      // * if user can see hotspot, then only hotspot is clickable
      return;
    }

    let responseId = generateRandomId();
    const position = stage.getPointerPosition();
    const payload = {
      ...position,
      responseId,
      id: isDropzone ? node.id() : null,
    };

    if (!allowToShowHotspot) {
      /**
       * @HOTSPOT_NOT_VISIBLE
       * When student can click any where in canvas
       */
      if (isRemoveAction) {
        responseId = _.replace(id, STUDENT_MARKED_BOOKMARK, "");
        submissionData = !allowToSelectMultipleHotspot
          ? []
          : _.filter(
              submissionData,
              item => _.get(item, "responseId") !== responseId
            );
      } else {
        submissionData = !allowToSelectMultipleHotspot
          ? [payload]
          : [payload, ...submissionData];
      }
    } else {
      /**
       * @HOTSPOT_VISIBLE
       * When student can see hotspot and can select among one of them
       * responseId will be same as id for this case
       */
      payload.responseId = payload.id;
      const isAlreadySelected = _.find(submissionData, { id: payload.id });
      if (isAlreadySelected) {
        submissionData = !allowToSelectMultipleHotspot
          ? []
          : _.filter(
              submissionData,
              item => _.get(item, "responseId") !== payload.responseId
            );
      } else {
        submissionData = !allowToSelectMultipleHotspot
          ? [payload]
          : [payload, ...submissionData];
        node.fill(HOTSPOT_CONFIG.SELECTED_HOTSPOT_FILL);
      }
    }

    allowStudentClick = false;
    if (_.isFunction(props?.onClick)) props.onClick(node, submissionData);
  };

  // * Update hotspot transparency on hovering
  const onMouseMove = e => {
    const node = e.target;
    const name = _.first(_.split(node.name(), "#"));

    if (activeHotspot) activeHotspot.fill("transparent");
    activeHotspot = null;

    if (name === "DROPZONE" && _.get(options, "allowToShowHotspot")) {
      const isAlreadySelected = _.find(submissionData, { id: node.id() });
      activeHotspot = node;
      node.fill(
        isAlreadySelected
          ? HOTSPOT_CONFIG.SELECTED_HOTSPOT_FILL
          : HOTSPOT_CONFIG.ACTIVE_HOTSPOT_FILL
      );
    }

    const touch = e?.evt?.touches?.[0];
    const dx = touch?.clientX - touchStartPos.x;
    const dy = touch?.clientY - touchStartPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > SWIPE_THRESHOLD) {
      touchMoved = true;
    }
  };

  // * check swipe action
  const onMouseDown = e => {
    const touch = _.get(e, ["evt", "touches", "0"]);
    if (touch) touchStartPos = { x: touch.clientX, y: touch.clientY };
    touchMoved = false;
  };

  listenBackEvents(stage, transformer, {
    onClick,
    onMouseMove,
    onMouseDown,
  });
};

/**
 *
 * @param {*} konvaInstance
 * @param {*} mode
 * @param {*} cb
 * @description Event listener for drag and drop question
 */
export const addFindHotspotQuestionEventListener = (
  konvaInstance = {},
  mode = "",
  cb = () => {}
) => {
  if (mode === "create") {
    addTeacherCreateModeEventListener(konvaInstance);
  } else if (mode === "edit" || mode === "preview") {
    addEditAndPreviewModeEventListener(konvaInstance, mode);
  }
  if (_.isFunction(cb)) cb();
};

/**
 *
 * @param {*} item
 * @returns Higest polygon mid point
 */
const findHighestMidPointInPolygon = node => {
  const x = node.x();
  const y = node.y();
  const points = node.points();

  // * Split coordinates into segments
  const segments = _.chunk(points, 2);
  const mids = [];

  // * Get all possible mid points
  for (let i = 1; i < segments.length; i++) {
    const coord1 = segments[i - 1];
    const coord2 = segments[i];
    const midX = (_.first(coord1) + _.first(coord2)) / 2;
    const midY = (_.last(coord1) + _.last(coord2)) / 2;
    mids.push([midX, midY]);
  }

  // * find highest mid point
  const highestPoint = _.minBy(mids, item => _.last(item));

  return [_.first(highestPoint) + x, _.last(highestPoint) + y];
};

/**
 *
 * @param {*} node
 * @returns Add tick on hotspot
 */
const addTickPositionOnHotspot = (
  node,
  { konva, layer, iconType = EVALUATION_STATUS.CORRECT }
) => {
  if (!node || !layer) return;

  const position = { x: node.x(), y: node.y() };
  const type = _.get(_.split(node.name(), "#"), "1");

  if (type === "ELLIPSE") {
    position.y = position.y - node.radiusY();
  } else if (type === "RECTANGLE") {
    position.x = position.x + node.width() / 2;
  } else if (type === "POLYGON") {
    const midPoint = findHighestMidPointInPolygon(node);
    position.x = _.first(midPoint) || 0;
    position.y = _.last(midPoint) || 0;
  }

  const relativeSize = getCanvasRelativeSize([16]);
  const iconSize = _.first(relativeSize);

  const imageNode = new konva.Image({
    id: `${node.id()}${TEACHER_MARKED_BOOKMARK}`,
    x: position.x - iconSize / 2,
    y: position.y - iconSize / 2,
    width: iconSize,
    height: iconSize,
    draggable: false,
    listening: false,
  });

  // * Use cached image
  if (cachedTickImage[iconType]) {
    imageNode.image(cachedTickImage[iconType]);
    layer.add(imageNode);
    return;
  }

  const tickImage = new Image();
  tickImage.src =
    iconType === EVALUATION_STATUS.CORRECT
      ? EVALUATION_MODE_CONFIG.CORRECT_ANSWER_SRC
      : EVALUATION_MODE_CONFIG.WRONG_ANSWER_SRC;
  tickImage.onload = function () {
    cachedTickImage[iconType] = this;
    imageNode.image(this);
    layer.add(imageNode);
  };
};

/**
 *
 * @param {*} node
 * @returns Add tick on hotspot
 */
const addTickOnCustomPosition = (
  position,
  { konva, layer, iconType = null }
) => {
  if (!layer) return;

  const relativeSize = getCanvasRelativeSize([41, 46, 36]);

  const imageNode = new konva.Image({
    id: `${_.get(position, "responseId", "")}${STUDENT_MARKED_BOOKMARK}`,
    x: position.x - relativeSize[0] / 2,
    y: position.y - relativeSize[2],
    width: relativeSize[0],
    height: relativeSize[1],
    draggable: false,
  });

  // * Use cached purple bookmark image
  if (cachedBookmarkIcons[iconType]) {
    imageNode.image(cachedBookmarkIcons[iconType]);
    layer.add(imageNode);
    return;
  }

  const tickImage = new Image();
  tickImage.src =
    iconType === EVALUATION_STATUS.CORRECT
      ? HOTSPOT_CONFIG.CORRECT_HOTSPOT_BOOKMARK_SRC
      : HOTSPOT_CONFIG.WRONG_HOTSPOT_BOOKMARK_SRC;
  tickImage.onload = function () {
    cachedBookmarkIcons[iconType] = this;
    imageNode.image(this);
    layer.add(imageNode);
  };
};

/**
 *
 * @param {*} mode
 * @param {*} param
 * @description action after drawing
 * 1. Adding tick marks and book marks based on condition of show hotspot to student
 * 2. Adding multiple hotspot if allowed to answer
 * 3. View solution and giving evalution mode view from here
 */
export const updateFindHotspotElementsAfterDrawing = (
  mode,
  { konva, stage, layer, options = {} }
) => {
  const {
    viewSolution = false,
    allowToShowHotspot = false,
    showViewSolutionButton = false,
    isTeacherStudentViewMode = false,
    studentAnswers = [],
    isSubmitted = false,
  } = options || {};

  if (mode === "create") {
    /**
     * On selection correct answer, Displaying tick icon
     * green hotspot stroke for marked hotspot
     */
    _.forEach(layer?.children, node => {
      const nodeName = _.split(node.name(), "#");
      if (_.first(nodeName) !== "DROPZONE") return;

      // * If preview mode, update hotspot stroke accordingly
      if (isTeacherStudentViewMode) {
        if (allowToShowHotspot) {
          node.stroke(HOTSPOT_CONFIG.STUDENT_HOTSPOT_FILL);
        } else {
          node.strokeWidth(0);
          if (stage) {
            const supportDash = stage.findOne(`#${node.id()}-dash-support`);
            if (supportDash) supportDash.strokeWidth(0);
          }
        }
        return;
      }

      if (!_.includes(_.get(options, "findHotspotAnswers"), node.id())) return;

      // * adding tick on correct marked hotspot
      addTickPositionOnHotspot(node, { layer, konva });
      node.stroke(HOTSPOT_CONFIG.CORRECT_HOTSPOT_FILL);
    });
  } else if (
    mode === "edit" ||
    mode === "preview" ||
    (mode === "view" && !showViewSolutionButton)
  ) {
    /**
     * For student submission mode add ticks and bookmarks on click
     */
    _.forEach(layer?.children, node => {
      const nodeName = _.split(node.name(), "#");
      if (_.first(nodeName) !== "DROPZONE") return;

      const id = node.id();
      const markedHotspot = _.find(studentAnswers, { id });

      if (!allowToShowHotspot) {
        // * Hide hotspot on question setting condition
        node.strokeWidth(0);
        if (stage) {
          const supportDash = stage.findOne(`#${node.id()}-dash-support`);
          if (supportDash) supportDash.visible(false);
        }
      }

      if (markedHotspot && allowToShowHotspot) {
        addTickPositionOnHotspot(node, { layer, konva });
        node.stroke(HOTSPOT_CONFIG.CORRECT_HOTSPOT_FILL);
      } else {
        node.stroke(HOTSPOT_CONFIG.STUDENT_HOTSPOT_FILL);
      }
    });

    if (!allowToShowHotspot) {
      /**
       * if hotspot is not allowed to show,
       * render all custom position points bookmark
       */
      _.forEach(studentAnswers, item => {
        addTickOnCustomPosition(item, {
          layer,
          konva,
          iconType: EVALUATION_STATUS.CORRECT,
        });
      });
    }
  } else if (
    (mode === "view" && showViewSolutionButton) ||
    mode === "solution_view"
  ) {
    if (viewSolution) {
      /**
       * In Evaluation mode or Show answer in draft WS,
       * Viewing correct solution to teacher
       */
      _.forEach(_.get(options, "findHotspotAnswers", []), nodeId => {
        const node = stage.findOne(`#${nodeId}`);
        if (!node) return;
        node.stroke(EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE);
        addTickPositionOnHotspot(node, { konva, layer });
      });
    } else if (isSubmitted) {
      /**
       * In Evaluation mode,
       * Showing student response for multiple condition
       */
      _.forEach(studentAnswers, item => {
        const isCorrect = _.includes(
          _.get(options, "findHotspotAnswers", []),
          _.get(item, "id")
        );

        if (allowToShowHotspot) {
          const node = stage.findOne(`#${_.get(item, "id")}`);
          if (!node) return;
          node.stroke(
            isCorrect
              ? EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE
              : EVALUATION_MODE_CONFIG.WRONG_ANSWER_STROKE
          );
          addTickPositionOnHotspot(node, {
            konva,
            layer,
            iconType: isCorrect
              ? EVALUATION_STATUS.CORRECT
              : EVALUATION_STATUS.WRONG,
          });
        } else {
          addTickOnCustomPosition(item, {
            layer,
            konva,
            iconType: isCorrect
              ? EVALUATION_STATUS.CORRECT
              : EVALUATION_STATUS.WRONG,
          });
        }
      });
    }
  }
};
